import {
    Mark,
    mergeAttributes,
    Range
} from '@tiptap/core'
import { Mark as PMMark, Node as PMNode } from "@tiptap/pm/model";
import { Plugin, PluginKey } from '@tiptap/pm/state'
import { Decoration, DecorationSet } from '@tiptap/pm/view'

export interface CommentOptions {
    /**
    * HTML attributes to add to the bold element.
    * @default {}
    * @example { class: 'foo' }
    */
    HTMLAttributes: Record<string, any>,
    onCommentActivated: (commentID: string | null) => void // what if handler would be wrapped with useRef?
}

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    comment: {
      setComment: (commentId: string) => ReturnType,
      unsetComment: (commentId: string) => ReturnType,
      retagComment: (oldCommentId: string, newCommentId: string) => ReturnType,
      setActiveComment: (commentId: string | null) => ReturnType,
    }
  }
}
export interface MarkWithRange {
    mark: PMMark;
    range: Range;
}

export interface CommentStore {
    activeCommentId: string | null
}

/**
 * Where every comment is currently anchored in the document, keyed by comment id. Marks are the
 * authoritative anchors: ProseMirror maps them through every edit, so they stay on the right text,
 * while the `pos` stored on a comment record is frozen at the moment the comment was created.
 * A comment split across several text nodes (part of it bold, say) reports one covering range.
 */
export function collectCommentRanges(doc: PMNode): Map<string, Range> {
    const ranges = new Map<string, Range>()
    doc.descendants((node, pos) => {
        for (const mark of node.marks) {
            if (mark.type.name !== 'comment' || !mark.attrs.commentId) continue

            const id = mark.attrs.commentId as string
            const prev = ranges.get(id)
            ranges.set(id, prev
                ? { from: Math.min(prev.from, pos), to: Math.max(prev.to, pos + node.nodeSize) }
                : { from: pos, to: pos + node.nodeSize })
        }
    })
    return ranges
}

interface ActiveCommentState {
    activeCommentId: string | null
    decorations: DecorationSet
}

export const activeCommentPluginKey = new PluginKey<ActiveCommentState>('activeComment')

export const AppComment = Mark.create<CommentOptions, CommentStore>({
    name: 'comment',

    // a comment covers exactly the text it was made on: typing at either edge of the mark starts
    // unmarked text rather than growing the comment. Typing inside it still extends it, since the
    // text nodes on both sides of the caret carry the mark.
    inclusive: false,

    addOptions() {
        return {
            HTMLAttributes: {},
            onCommentActivated: () => {},
        }
    },

    addAttributes() {
        return {
            commentId: {
                default: null,
                parseHTML: (el) =>
                    (el as HTMLSpanElement).getAttribute("data-comment-id"),
                renderHTML: (attrs) => ({ "data-comment-id": attrs.commentId }),
            },
        }
    },

    onSelectionUpdate() {
        const { $from } = this.editor.state.selection;
        const marks = $from.marks()

        if (marks.length === 0) {
            this.storage.activeCommentId = null
            this.options.onCommentActivated(null)
            return
        }

        const cMark = this.editor.schema.marks.comment
        const activeComment = marks.find((m) => m.type === cMark)

        this.storage.activeCommentId = activeComment?.attrs.commentId || null
        this.options.onCommentActivated(this.storage.activeCommentId)
    },

    addStorage() {
        return {
            activeCommentId: null
        }
    },

    parseHTML() {
        return [
            {
            tag: "span[data-comment-id]",
            getAttrs: (el) =>
                !!(el as HTMLSpanElement).getAttribute("data-comment-id")?.trim() &&
                null,
            },
        ]
    },

    renderHTML({ HTMLAttributes }) {
        return ['span', mergeAttributes(this.options.HTMLAttributes, HTMLAttributes), 0]
    },

    addCommands() {
        return {
            setComment: (commentId) => ({ commands }) => {
                return commands.setMark("comment", { commentId })
            },
            unsetComment: (commentId) => ({ tr, dispatch }) => {
                if (!commentId) return false

                const commentMarksWithRange: MarkWithRange[] = []

                tr.doc.descendants((node, pos) => {
                    const commentMark = node.marks.find(
                    (mark) =>
                        mark.type.name === "comment" &&
                        mark.attrs.commentId === commentId,
                    )

                    if (!commentMark) return

                    commentMarksWithRange.push({
                        mark: commentMark,
                        range: {
                            from: pos,
                            to: pos + node.nodeSize,
                        },
                    })
                })

                if (commentMarksWithRange.length === 0) return false

                commentMarksWithRange.forEach(({ mark, range }) => {
                    tr.removeMark(range.from, range.to, mark)
                })

                return dispatch?.(tr)
            },
            retagComment: (oldCommentId, newCommentId) => ({ tr, dispatch }) => {
                if (!oldCommentId || !newCommentId) return false

                const commentMarksWithRange: MarkWithRange[] = []

                tr.doc.descendants((node, pos) => {
                    const commentMark = node.marks.find(
                    (mark) =>
                        mark.type.name === "comment" &&
                        mark.attrs.commentId === oldCommentId,
                    )

                    if (!commentMark) return

                    commentMarksWithRange.push({
                        mark: commentMark,
                        range: {
                            from: pos,
                            to: pos + node.nodeSize,
                        },
                    })
                })

                if (commentMarksWithRange.length === 0) return false

                commentMarksWithRange.forEach(({ mark, range }) => {
                    tr.removeMark(range.from, range.to, mark)
                    tr.addMark(range.from, range.to, mark.type.create({ ...mark.attrs, commentId: newCommentId }))
                })

                return dispatch?.(tr)
            },
            setActiveComment: (commentId) => ({ tr, dispatch }) => {
                tr.setMeta(activeCommentPluginKey, { activeCommentId: commentId })
                return dispatch?.(tr) ?? false
            },
        }
    },

    addProseMirrorPlugins() {
        return [
            new Plugin<ActiveCommentState>({
                key: activeCommentPluginKey,
                state: {
                    init(): ActiveCommentState {
                        return { activeCommentId: null, decorations: DecorationSet.empty }
                    },
                    apply(tr, prev): ActiveCommentState {
                        const meta = tr.getMeta(activeCommentPluginKey)
                        const activeCommentId: string | null = meta === undefined
                            ? prev.activeCommentId
                            : meta.activeCommentId
                        if (!activeCommentId) {
                            return { activeCommentId: null, decorations: DecorationSet.empty }
                        }
                        // same comment, untouched doc - the existing decorations still hold
                        if (meta === undefined && !tr.docChanged) return prev

                        // the highlight is derived from the marks, so it has to be rebuilt whenever the
                        // doc changes. Mapping the old decorations forward would keep highlighting text
                        // whose mark has since been removed - which is what happens when the active
                        // comment is deleted - and would miss text the mark has grown over.
                        const decos: Decoration[] = []
                        tr.doc.descendants((node, pos) => {
                            const mark = node.marks.find(
                                m => m.type.name === 'comment' && m.attrs.commentId === activeCommentId
                            )
                            if (mark) {
                                decos.push(Decoration.inline(pos, pos + node.nodeSize, { class: 'comment-active' }))
                            }
                        })
                        return { activeCommentId, decorations: DecorationSet.create(tr.doc, decos) }
                    },
                },
                props: {
                    decorations(state) {
                        return this.getState(state)?.decorations ?? DecorationSet.empty
                    },
                },
            }),
        ]
    },
})
